import { z } from 'zod';
import { autonomyLevelSchema } from './brand-brain.js';
// Trust ledger (s210). Spec §3.3 + §5 — shared shapes between BE
// recording side-effects, the FE trust view, the autonomy-migration
// proposer, and (s211+) the enforcement layer that reads
// brand.authorityMap before auto-applying.
//
// Reuses the existing AutonomyLevel + authorityMapSchema from
// brand-brain.ts — those govern the founder's STATED preferences
// (set at onboarding). brands.authorityMap is the LIVE per-brand
// authority the trust ledger mutates on migration approval; same
// shape, different lifecycle.
// ─── Decision types ────────────────────────────────────────────────
//
// Coarser than feed card kinds. Multiple kinds collapse to one
// decision type so "how trustworthy is Mag on creative" is a
// meaningful aggregate even though concept_proposal +
// creative_variants are different surfaces.
//
// Adding a new type:
//   1. Add the enum value here
//   2. Map any new card kinds to it in cardKindToDecisionType
//   3. Choose a default authority in DEFAULT_AUTHORITY_MAP
// No schema migration needed — trust_ledger_entries.decision_type
// is varchar.
export const decisionTypeSchema = z.enum([
    /** brand-coach diffs to the brain. The default-A2 (always-ask)
     *  category — brain edits propagate everywhere, founder approval
     *  bar should stay high until trust accrues. */
    'brain_edit',
    /** Creative proposals + variant generation — concept_proposal +
     *  creative_variants cards. */
    'creative_direction',
    /** Campaign-level reads + recommendations from the scheduled
     *  scanner — campaign_insight + analytics_finding cards. */
    'campaign_recommendation',
    /** Open mag_followup resolutions (done / drop / working). Tracks
     *  founder follow-through on Mag's asks. */
    'followup_resolution',
    /** Onboarding / setup nudges — campaign_nudge,
     *  analytics_install_nudge. Acks tend to be "i'll get to it"
     *  rather than substantive approvals; tracked separately so they
     *  don't inflate streaks on the meatier categories. */
    'setup_nudge',
    // ─── Autopilot decision types (the autopilot roadmap (CommitMind) A0) ────
    //
    // Three channels × four decisions, plus google's bidding lane = 13.
    // Resolved per
    // (brand, campaign). Defaults are A1 for everything — "off by
    // default" is the non-negotiable constraint of the autopilot
    // roadmap; a campaign with no overrides resolves every decision
    // to A1 (Mag proposes, founder approves).
    //
    // Channel definitions:
    //   meta_organic — Instagram posts via the organic_meta campaign
    //     kind. No spend.
    //   meta_paid    — Meta ads via meta_paid_ad deliverables on
    //     creative_ads campaigns. Real spend.
    //   google_paid  — Google ads via google_search_ad deliverables
    //     on creative_ads campaigns. Real spend.
    //
    // Decision definitions (per channel):
    //   *_publish  — when content actually ships
    //   *_plan     — when the strategy/calendar gets revised
    //   *_creative — when drafts get written or rewritten
    //   *_evolve   — when the brand brain auto-applies diffs from
    //     this channel's signal (brand-coach)
    'meta_organic_publish',
    'meta_organic_plan',
    'meta_organic_creative',
    'meta_organic_evolve',
    'meta_paid_publish',
    'meta_paid_plan',
    'meta_paid_creative',
    'meta_paid_evolve',
    'google_paid_publish',
    'google_paid_plan',
    'google_paid_creative',
    'google_paid_evolve',
    'google_paid_bidding',
    // ─── Cross-channel decision (the autopilot roadmap (CommitMind) A7) ──────
    //
    // Highest-stakes decision in the product. The proposer reads
    // both paid channels and asks the founder to shift budget. A0
    // is INTENTIONALLY unreachable for this type — real money can
    // only move on an explicit founder click per the autopilot
    // roadmap.
    //
    // Streaks still accrue on this type so mag can show the founder
    // that her budget calls have been landing, but the trust-
    // migration proposer skips this type (NO_GRADUATION_DECISION_TYPES
    // below) so no A1→A0 card ever fires for it.
    'cross_channel_budget',
    // ─── Paid strategy refresh (A5-rotate / A6-rotate, revised) ────
    //
    // Daily scheduler emits a paid_strategy_refresh_proposal card
    // when a campaign's strategy is exhausted/stale. Founder accept
    // → strategy proposer runs. Same agency-coordination shape as
    // cross_channel_budget: A0 unreachable so mag never silently
    // rotates strategy. Streaks accrue for observability.
    'paid_strategy_refresh',
]);
/** Decision types where A0 is unreachable by design — the trust-
 *  migration proposer skips these when looking for streak-based
 *  graduations. Includes anywhere mag spends real strategic
 *  capital that the PR-agency model says belongs to the founder
 *  (cross-channel budget shifts, paid strategy rotation). */
export const NO_GRADUATION_DECISION_TYPES = new Set([
    'cross_channel_budget',
    'paid_strategy_refresh',
]);
/** Subset of DecisionType covering the 12 autopilot lanes. Used
 *  by the per-campaign override layer + the toggle UI; the trust-
 *  ledger functions stay agnostic. */
export const autopilotDecisionTypeSchema = z.enum([
    'meta_organic_publish',
    'meta_organic_plan',
    'meta_organic_creative',
    'meta_organic_evolve',
    'meta_paid_publish',
    'meta_paid_plan',
    'meta_paid_creative',
    'meta_paid_evolve',
    'google_paid_publish',
    'google_paid_plan',
    'google_paid_creative',
    'google_paid_evolve',
    'google_paid_bidding',
]);
/** Autopilot channel keys — for grouping decisions in UI + for
 *  selecting which subset of decision types to bundle when a
 *  campaign's autopilot toggle flips. */
export const autopilotChannelSchema = z.enum(['meta_organic', 'meta_paid', 'google_paid']);
/** Per-campaign authority overrides. Resolution order is
 *  campaign-override → brand-default → DEFAULT_AUTHORITY_MAP.
 *  Stored as jsonb on campaigns.authority_overrides; missing keys
 *  fall through to the brand layer. */
// PARTIAL, NOT EXHAUSTIVE, and the distinction is not cosmetic.
//
// zod 4's z.record() over an ENUM key requires EVERY member of that enum to be
// present — a map with one of the ~34 decision types fails to parse, listing
// the other 33 as missing. z.partialRecord is the shape that means "some of
// these keys", which is what an authority map has always been: setAuthority
// merges ONE key at a time, so every map it writes is partial by construction.
//
// With z.record this schema could not parse any map the product actually
// writes, and grantedBaseline's `if (!parsed.success) return
// DEFAULT_AUTHORITY_MAP[...]` turned that into a SILENT fallback: a founder's
// authority grant was stored, displayed, and then ignored at read time.
// Failing toward the static default is fail-safe in direction, which is
// exactly why it surfaced as nothing at all rather than as an error.
//
// Keys and values are still validated — partialRecord rejects an unknown
// decision type and an unknown level. Only the all-keys-required part goes.
export const campaignAuthorityOverridesSchema = z.partialRecord(decisionTypeSchema, autonomyLevelSchema);
/** What the founder did to a Mag proposal. */
export const trustVerdictSchema = z.enum([
    'approved', // acked / explicitly applied (e.g. apply-brand-evolution)
    'dismissed', // skipped with no positive action
    'overridden', // founder did something different (reserved for s211+)
]);
// ─── Authority model (spec §5) ─────────────────────────────────────
//
// AutonomyLevel + authorityMapSchema are imported from brand-brain.ts
// (they were defined there first, for the spec's
// preferences.decision_authority_map field). The shape lines up
// exactly — same A0..A3 enum, same jsonb record. The trust-ledger
// side adds a typed-key view + sensible defaults per decision type.
//
// A0 — auto: Mag executes, logs, mentions in next recap
// A1 — propose, default Yes: Mag proposes; executes if no objection in N hours
// A2 — propose, default No: Mag proposes; waits for explicit approval
// A3 — ask: Mag describes situation; founder decides from scratch
//
// s210 SHIPS the model but does NOT enforce A0 anywhere — every diff
// still requires explicit approval today. s211 wires the actual
// enforcement (brand-coach reads authority for brain_edit, etc.).
/** Strict typed-key version of the authority map for proposers + UI.
 *  brands.authorityMap stores the loose `Record<string, AutonomyLevel>`
 *  form for forward-compat (adding a decision type doesn't need a
 *  migration); readers parse through this stricter shape. */
// Partial for the same reason as campaignAuthorityOverridesSchema above —
// this is the one that was actually being read, by grantedBaseline.
export const decisionTypedAuthorityMapSchema = z.partialRecord(decisionTypeSchema, autonomyLevelSchema);
/** Default authority per decision type. AIaS reframe (spec §0a.2):
 *  trust starts HIGH on the boring autonomy and DEGRADES on mistakes.
 *  Day-0 founders sit at A0/A1 for the routine autonomy; mistakes per
 *  (brand, decision_type) drop levels via trust_authority_drops.
 *
 *  - brain_edit stays at A2 (brain-edit propagates everywhere; high
 *    bar appropriate even under AIaS — the founder owns identity).
 *  - creative_direction stays at A2 (founder taste call).
 *  - campaign_recommendation stays at A2 (strategic observations
 *    benefit from founder weighting).
 *  - followup_resolution / setup_nudge stay at A3 (only the founder
 *    knows the actual answer).
 *
 *  Autopilot lanes flip from A1 (proposer pattern) → A0/A1 per spec
 *  §0a.2 inverted table:
 *    - *_publish (paid + organic) → A0 — boring time-sensitive
 *      autonomy; pauses, rotations, scheduled publishes
 *    - *_creative → A1 — drafting is fine to proceed if no objection;
 *      generated copy can wait a few hours for founder review
 *    - *_plan → A1 — plan changes should be visible before they ship
 *    - *_evolve → A2 — brand-brain edits touch identity; high bar
 *
 *  cross_channel_budget flips to A0 — within the brand's monthly
 *  mistake budget. Beyond the budget mag escalates per spec.
 *  paid_strategy_refresh stays A1 — strategic enough to deserve
 *  founder eyes before runs land. */
export const DEFAULT_AUTHORITY_MAP = Object.freeze({
    brain_edit: 'A2',
    creative_direction: 'A2',
    campaign_recommendation: 'A2',
    followup_resolution: 'A3',
    setup_nudge: 'A3',
    // Organic publish stays autonomous by default. Reversible, spends no
    // money, and gated separately: brand_channels.autopilot defaults to
    // false for new channels (migration 0164), which is the consent model
    // for this lane.
    meta_organic_publish: 'A0',
    // PAID publish is NOT autonomous by default — spec 70247db7.
    //
    // It used to be. Both lanes sat at A0 here, which meant every brand on
    // in_house or above spent money autonomously from the moment its card
    // cleared, having clicked nothing. The measurement that settled it
    // (decision 0eca1e2): across the whole production database, zero brands
    // and zero campaigns had ever granted autonomy explicitly, while one
    // brand had already had four paid publishes executed on its behalf.
    //
    // A2 rather than A1 because A1 still ships on silence, and silence is
    // exactly what the Terms' liability clause cannot rest on.
    //
    // Two routes up, both founder actions, both already built: accept a
    // trust-migration card (needs an approved streak of 10, one level per
    // accept), or switch on autopilot for a campaign. Nothing here grants
    // A0 on its own — that is the whole point.
    meta_paid_publish: 'A2',
    google_paid_publish: 'A2',
    // Creative drafting — mag generates, proposes; ships if no
    // objection. The bench primitive (hook rotation) reads at A0 by
    // exception inside the scheduler — see Phase 1A.
    meta_organic_creative: 'A1',
    meta_paid_creative: 'A1',
    google_paid_creative: 'A1',
    // Plan changes — content plan revisions, audience tweaks. Visible
    // before they ship.
    meta_organic_plan: 'A1',
    meta_paid_plan: 'A1',
    google_paid_plan: 'A1',
    // Bid strategy — real money, and the damage from a wrong one is SILENT:
    // a bad target throttles a campaign toward zero impressions while the
    // campaign keeps existing and keeps looking healthy. A2 for the same
    // reason paid publish is A2 rather than A1 — A1 ships on silence, and
    // this is not a lane where silence should move money. Only an explicit
    // autopilot toggle raises it (AUTOPILOT_ON_LEVELS below).
    google_paid_bidding: 'A2',
    // Brain edits from a channel's signal — touches identity, high bar.
    meta_organic_evolve: 'A2',
    meta_paid_evolve: 'A2',
    google_paid_evolve: 'A2',
    // Cross-channel budget — autonomous within the brand's mistake
    // budget. Beyond it, mag escalates one level (A0 → A1). NO_GRADUATION
    // still keeps the trust-migration proposer from offering A0 as a
    // streak-recovery — recovery happens via clean_streak_recovery on
    // the drops ledger, not founder approval.
    cross_channel_budget: 'A0',
    // Paid strategy refresh — strategic; even high trust keeps this at
    // A1 so the founder can shape the framing.
    paid_strategy_refresh: 'A1',
});
/** The 4 decision types in one channel, used when bundling
 *  overrides for the per-campaign autopilot toggle. */
export const AUTOPILOT_DECISIONS_BY_CHANNEL = Object.freeze({
    meta_organic: [
        'meta_organic_publish',
        'meta_organic_plan',
        'meta_organic_creative',
        'meta_organic_evolve',
    ],
    meta_paid: ['meta_paid_publish', 'meta_paid_plan', 'meta_paid_creative', 'meta_paid_evolve'],
    // FIVE, not four — google_paid is the one channel with a bidding lane,
    // because it is the only channel whose bid strategy we can read and write.
    google_paid: [
        'google_paid_publish',
        'google_paid_plan',
        'google_paid_creative',
        'google_paid_evolve',
        'google_paid_bidding',
    ],
});
/** Stakes-aware authority bundle written when an autopilot toggle
 *  flips ON.
 *   - *_publish + *_evolve → A0 across all channels (mag acts).
 *   - meta_organic_creative → A0 (phase A2 — autonomous rewrite on
 *     underperformer-conflict slots, with revert window).
 *   - meta_paid_creative → A0 (phase A5-finish — autonomous draft-
 *     from-strategy-angle. Lands a fresh meta_paid_ad deliverable
 *     with empty image_url so launch still gates on the founder's
 *     image render or paste-in.)
 *   - *_plan stays A1 (mag proposes plan revisions via chat; full
 *     autonomous re-planning is phase A5+).
 *   - google_paid_creative → A0 (A6-finish shipped the google paid
 *     autodrafter; this line previously said it stayed A1 "until parity
 *     ships", which was true when written and stopped being true in the
 *     next commit. A founder flipping the toggle DOES grant autonomous
 *     google creative.)
 *  Per-decision overrides within ON state are a future phase. */
export const AUTOPILOT_ON_LEVELS = Object.freeze({
    meta_organic_publish: 'A0',
    meta_organic_plan: 'A1',
    meta_organic_creative: 'A0',
    meta_organic_evolve: 'A0',
    meta_paid_publish: 'A0',
    meta_paid_plan: 'A1',
    meta_paid_creative: 'A0',
    meta_paid_evolve: 'A0',
    google_paid_publish: 'A0',
    google_paid_plan: 'A1',
    google_paid_creative: 'A0',
    google_paid_evolve: 'A0',
    // A0 UNDER AUTOPILOT, A2 BY DEFAULT — spec 3c0ba7ae. Moving a bid
    // strategy is exactly the kind of thing an agency does without asking,
    // which is the founder's own framing, and it is also not cleanly
    // undoable: the swap resets google's learning phase.
    //
    // It gets its OWN lane rather than borrowing one, and both obvious
    // alternatives are wrong. google_paid_evolve is A0 already but means
    // brand-brain identity edits, so filing bidding there would couple two
    // unrelated grants — the lane-misfiling that made d4779222 dead on
    // arrival. google_paid_plan is semantically right but is pinned at A1
    // just below, deliberately, and raising it would hand over every other
    // plan revision as a side effect of wanting bidding.
    google_paid_bidding: 'A0',
});
// A0 unreachable for paid_strategy_refresh — default A1 (mag
// proposes via card, founder accepts/dismisses).
// NO_GRADUATION_DECISION_TYPES suppresses A1→A0 migration cards.
// Default seed for the brand authority map.
// (The strict default-map check already exists per the
// DEFAULT_AUTHORITY_MAP record; add paid_strategy_refresh below.)
/** Feed card kinds that map to a decision type. Cards not in this
 *  table (welcome, mag_chat, mag_followup_complete, brand_evolution
 *  resolution itself) don't get a ledger entry — they're not
 *  "approve this Mag proposal" decisions. */
const CARD_KIND_TO_DECISION_TYPE = {
    brand_evolution: 'brain_edit',
    concept_proposal: 'creative_direction',
    creative_variants: 'creative_direction',
    // s216 — drops are creative_direction too. Note: a single drop
    // card can produce N ledger entries (one per concept the founder
    // acts on), which is the right shape — each concept is its own
    // decision and trust accrues per-concept.
    creative_drop: 'creative_direction',
    campaign_insight: 'campaign_recommendation',
    analytics_finding: 'campaign_recommendation',
    mag_followup: 'followup_resolution',
    campaign_nudge: 'setup_nudge',
    analytics_install_nudge: 'setup_nudge',
    // A7 — cross-channel budget proposal. apply / dismiss accrue
    // streaks on `cross_channel_budget` for observability; the
    // migration proposer skips this type (see NO_GRADUATION_DECISION_TYPES).
    cross_channel_budget_proposal: 'cross_channel_budget',
    // A5-rotate / A6-rotate revised — paid strategy refresh
    // proposal. accept / dismiss accrue streaks on
    // paid_strategy_refresh; A0 unreachable so no migration prompt.
    paid_strategy_refresh_proposal: 'paid_strategy_refresh',
};
export function cardKindToDecisionType(cardKind) {
    return CARD_KIND_TO_DECISION_TYPE[cardKind] ?? null;
}
// ─── Trust migration proposal card payload ─────────────────────────
/** Payload for the trust_migration_proposal feed card kind. Lands
 *  when the proposer detects a long-enough approval streak on a
 *  decision type whose authority is currently A1+. */
export const trustMigrationProposalPayloadSchema = z.object({
    kind: z.literal('trust_migration_proposal'),
    decisionType: decisionTypeSchema,
    currentAuthority: autonomyLevelSchema,
    proposedAuthority: autonomyLevelSchema,
    /** Consecutive approvals counted at the time the proposal was
     *  generated. Embedded in the payload so the founder sees the
     *  exact number Mag is citing, not a live-recomputed value that
     *  may have moved on. */
    approvalStreak: z.number().int().min(1),
    /** Window the streak accumulated over, in days. Pre-computed at
     *  proposal time. */
    windowDays: z.number().int().min(1),
    /** Mag-voice one-paragraph rationale. "i've nailed the brain-edit
     *  call 12 times in a row over the last 8 weeks — want me to
     *  auto-apply the low-risk ones?" */
    rationale: z.string().min(1).max(600),
});
//# sourceMappingURL=trust-ledger.js.map